<script setup>
import { ref, computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import { useSectorlessAccount } from 'dashboard/composables/useSectorlessAccount';
import { useSectorPermissions } from 'dashboard/composables/useSectorPermissions';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  sectorMemberships: {
    type: Array,
    default: () => [],
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { isSectorlessAccount } = useSectorlessAccount();
const { t } = useI18n();

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const getSectors = useMapGetter('sectors/getSectors');
const getTeams = useMapGetter('teams/getTeams');

const { isAccountAdmin, isSectorAdmin, hasSectorMembership } =
  useSectorPermissions();

const firstMembership = props.sectorMemberships?.[0];
const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedTeams = ref(
  getTeams.value.filter(t => (props.teamIds || []).includes(t.id))
);
const selectedRoleId = ref(
  props.customRoleId ||
    (!isSectorlessAccount.value && firstMembership?.role) ||
    props.type
);
const selectedSectorId = ref(firstMembership?.sector_id || null);
watch(isSectorlessAccount, sectorless => {
  selectedRoleId.value =
    props.customRoleId || (!sectorless && firstMembership?.role) || props.type;
});
const agentCredentials = ref({ email: props.email });

const isSectorRole = computed(() =>
  ['supervisor', 'sector_admin', 'agent'].includes(selectedRoleId.value)
);

const showSectorField = computed(
  () => !isSectorlessAccount.value && isSectorRole.value
);

const rules = computed(() => ({
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
  selectedSectorId: { required: requiredIf(() => showSectorField.value) },
}));

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
  selectedSectorId,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const roles = computed(() => {
  const accountRoles = isAccountAdmin.value
    ? [
        {
          id: 'administrator',
          name: 'administrator',
          label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
        {
          id: 'agent',
          name: 'agent',
          label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
        },
      ]
    : [];

  const sectorAdminRole =
    isAccountAdmin.value || isSectorAdmin.value
      ? [
          {
            id: 'sector_admin',
            name: 'sector_admin',
            label: t('AGENT_MGMT.AGENT_TYPES.SECTOR_ADMIN'),
          },
        ]
      : [];

  const supervisorRole =
    isAccountAdmin.value || hasSectorMembership.value
      ? [
          {
            id: 'supervisor',
            name: 'supervisor',
            label: t('AGENT_MGMT.AGENT_TYPES.SUPERVISOR'),
          },
          {
            id: 'agent',
            name: 'agent',
            label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
          },
        ]
      : [];

  const customRoles = isAccountAdmin.value
    ? getCustomRoles.value.map(role => ({
        id: role.id,
        name: `custom_${role.id}`,
        label: role.name,
      }))
    : [];

  return [
    ...accountRoles,
    ...sectorAdminRole,
    ...supervisorRole,
    ...customRoles,
  ].filter(
    role =>
      !isSectorlessAccount.value ||
      !['supervisor', 'sector_admin'].includes(role.id)
  );
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const availableTeams = computed(() => {
  if (!showSectorField.value || !selectedSectorId.value) {
    return getTeams.value;
  }

  return getTeams.value.filter(team => team.sector_id === selectedSectorId.value);
});

watch([showSectorField, selectedSectorId, getTeams], () => {
  const allowedTeamIds = new Set(availableTeams.value.map(team => team.id));
  selectedTeams.value = selectedTeams.value.filter(team =>
    allowedTeamIds.has(team.id)
  );
});

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      team_ids: selectedTeams.value.map(t => t.id),
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else if (showSectorField.value) {
      payload.role = 'agent';
      payload.role_in_sector = selectedRole.value.name;
      payload.sector_id = selectedSectorId.value;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="showSectorField" class="w-full">
        <label :class="{ error: v$.selectedSectorId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.LABEL') }}
          <select
            v-model="selectedSectorId"
            @change="v$.selectedSectorId.$touch"
          >
            <option :value="null" disabled>
              {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.PLACEHOLDER') }}
            </option>
            <option
              v-for="sector in getSectors"
              :key="sector.id"
              :value="sector.id"
            >
              {{ sector.name }}
            </option>
          </select>
          <span v-if="v$.selectedSectorId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.SECTOR.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full mb-2">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('AGENT_MGMT.ADD.FORM.TEAMS.LABEL') }}
        </label>
        <multiselect
          v-model="selectedTeams"
          :options="availableTeams"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="$t('AGENT_MGMT.ADD.FORM.TEAMS.PLACEHOLDER')"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        >
          <template #noOptions>{{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}</template>
        </multiselect>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml' && provider !== 'oracle'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
